import { useMemo, useState, type ReactElement } from 'react';
import { computeLineDiff, type VersionEntry } from '../lib/markdown-line-diff';

type Props = {
  oldText: string;
  newText: string;
  height?: number;
  /** Доступные версии для выбора базовой для сравнения. */
  versions?: VersionEntry[];
};

const LINE_HEIGHT_PX = 22;

function DiffLineRow({ line }: { line: { kind: 'same' | 'added' | 'removed'; value: string } }): ReactElement {
  const bg =
    line.kind === 'added'
      ? 'bg-[rgb(220_252_231_/_0.5)] dark:bg-[rgb(22_101_52_/_0.2)]'
      : line.kind === 'removed'
        ? 'bg-[rgb(254_202_202_/_0.5)] dark:bg-[rgb(153_27_27_/_0.2)]'
        : '';
  const textColor =
    line.kind === 'added'
      ? 'text-[rgb(22_101_52)] dark:text-[rgb(134_239_172)]'
      : line.kind === 'removed'
        ? 'text-[rgb(153_27_27)] dark:text-[rgb(252_165_165)]'
        : 'text-[hsl(var(--ui-muted-foreground))]';
  const gutter =
    line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ' ';

  return (
    <div className={`flex min-h-[${LINE_HEIGHT_PX}px] w-full font-mono text-sm leading-[${LINE_HEIGHT_PX}px] ${bg}`}>
      <span
        className={`inline-block w-8 shrink-0 select-none text-right text-xs leading-[${LINE_HEIGHT_PX}px] ${
          line.kind === 'added'
            ? 'text-[rgb(22_101_52)] dark:text-[rgb(134_239_172)]'
            : line.kind === 'removed'
              ? 'text-[rgb(153_27_27)] dark:text-[rgb(252_165_165)]'
              : 'text-[hsl(var(--ui-muted-foreground)/0.4)]'
        }`}
      >
        {gutter}
      </span>
      <span
        className={`inline-block flex-1 whitespace-pre-wrap break-all pl-2 leading-[${LINE_HEIGHT_PX}px] ${textColor}`}
      >
        {line.value || ' '}
      </span>
    </div>
  );
}

export function StaffMarkdownDiffView({ oldText, newText, height, versions }: Props): ReactElement {
  const versionEntries = useMemo(() => versions ?? [], [versions]);
  const defaultBaseline = versionEntries.length > 0 ? versionEntries[versionEntries.length - 1].version : -1;
  const [selectedVersion, setSelectedVersion] = useState<number>(defaultBaseline);

  const baselineText = useMemo(() => {
    if (selectedVersion < 0) return oldText;
    const entry = versionEntries.find((v) => v.version === selectedVersion);
    return entry ? entry.text : oldText;
  }, [selectedVersion, versionEntries, oldText]);

  const lines = useMemo(
    () => computeLineDiff(baselineText, newText),
    [baselineText, newText]
  );

  const hasDiff = lines.some((l) => l.kind !== 'same');

  return (
    <div
      className="overflow-auto overscroll-y-auto rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background))]"
      style={{ height: height ?? 'auto', maxHeight: height ?? 480 }}
    >
      <div className="sticky top-0 z-[1] flex flex-wrap items-center gap-3 border-b border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-3 py-1.5 text-xs text-[hsl(var(--ui-muted-foreground))]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[rgb(220_252_231)] dark:bg-[rgb(22_101_52_/_0.4)]" />
          Добавлено
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[rgb(254_202_202)] dark:bg-[rgb(153_27_27_/_0.4)]" />
          Удалено
        </span>

        {versionEntries.length > 0 ? (
          <span className="ml-auto flex items-center gap-1.5">
            <span>Сравнить с версией:</span>
            <select
              className="rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background))] px-2 py-0.5 text-xs text-[hsl(var(--ui-foreground))] outline-none"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
            >
              {versionEntries.map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}{v.date ? ` — ${new Date(v.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                </option>
              ))}
            </select>
          </span>
        ) : (
          <span className="ml-auto">
            {lines.filter((l) => l.kind !== 'same').length} изменений
          </span>
        )}
      </div>

      {!hasDiff ? (
        <div
          className="flex items-center justify-center px-3 text-sm text-[hsl(var(--ui-muted-foreground))]"
          style={{ minHeight: Math.max(60, (height ?? 120) - 32) }}
        >
          Нет изменений
        </div>
      ) : (
        <div className="px-1 py-1">
          {lines.map((line, i) => (
            <DiffLineRow key={i} line={line} />
          ))}
        </div>
      )}
    </div>
  );
}
